import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Check } from "lucide-react";

const BANNER_IMAGE =
  "https://images.unsplash.com/photo-1649514529778-600e5a445a16?crop=entropy&cs=srgb&fm=jpg&q=85";

function useReveal(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]:not(.in-view)");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  useReveal([services]);

  return (
    <div>
      {/* ── Page Hero Banner ─────────────────────────────────────────── */}
      <div className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img
          src={BANNER_IMAGE}
          alt="Photography studio"
          className="absolute inset-0 w-full h-full object-cover scale-105"
          style={{ objectPosition: "center 40%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        <div className="container-x relative z-10 h-full flex flex-col justify-end pb-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-3">
            Allure Image Studio
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">
            Photoshoot packages &amp;{" "}
            <span className="italic text-[hsl(var(--gold))]">pricing</span>
          </h1>
          <p className="mt-3 text-sm text-white/65 max-w-md">
            Every session is intentional. Pick the package that fits your story.
          </p>
        </div>
      </div>

      {/* ── Thin gold divider ────────────────────────────────────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent" />

      {/* ── Service list ─────────────────────────────────────────────── */}
      <div className="container-x py-14 md:py-24">
        <div className="space-y-20 md:space-y-32">
          {services.map((s, idx) => (
            <article
              key={s.id}
              className="grid md:grid-cols-12 gap-6 md:gap-12 items-start"
              data-testid={`service-${s.slug}`}
              data-reveal
            >
              {/* Image */}
              <div className={`md:col-span-6 ${idx % 2 ? "md:order-2" : ""}`}>
                <div className="aspect-[4/5] overflow-hidden bg-muted group">
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-6 md:py-4">
                <div className="eyebrow mb-3">{s.category}</div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-tight">
                  {s.name}
                </h2>
                <p className="mt-4 text-base text-foreground/65 leading-relaxed">
                  {s.description}
                </p>

                {/* Tiers */}
                <ul className="mt-8 border-t border-border">
                  {s.tiers.map((t) => (
                    <li
                      key={t.name}
                      className="py-5 border-b border-border group/tier hover:bg-muted/50 -mx-4 px-4 transition-colors"
                    >
                      <div className="flex items-baseline justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-serif text-xl">{t.name}</div>
                          </div>
                          {t.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {t.description}
                            </div>
                          )}
                          {t.deliverables?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {t.deliverables.slice(0, 4).map((d) => (
                                <li
                                  key={d}
                                  className="flex items-center gap-2 text-xs text-muted-foreground"
                                >
                                  <Check className="w-3 h-3 text-[hsl(var(--gold))] shrink-0" />
                                  {d}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-serif text-2xl">${t.price.toFixed(0)}</div>
                          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
                            {t.duration_minutes} min
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/booking?service=${s.slug}`}
                  className="mt-8 inline-flex items-center gap-3 px-7 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors group"
                  data-testid={`book-${s.slug}-btn`}
                >
                  Book {s.name}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
